import { colorsTuple, createTheme, defaultVariantColorsResolver, Loader, Progress, Stepper, VariantColorsResolver } from '@mantine/core'
import { Colors } from './colors'
import { Sizes } from './tokens'

const variantColorResolver: VariantColorsResolver = (input) => {
  const defaults = defaultVariantColorsResolver(input)
  const { variant, color, theme } = input
  const isPrimary = color === 'king' || color === theme.primaryColor

  if (isPrimary && variant === 'filled') {
    return {
      background: Colors.King,
      hover: Colors.Cobalt,
      color: Colors.White,
      border: 'transparent',
    }
  }

  if (isPrimary && variant === 'outline') {
    return {
      background: Colors.White,
      hover: Colors.Topaze,
      color: Colors.King,
      border: `1px solid ${Colors.King}`,
    }
  }

  if (isPrimary && variant === 'subtle') {
    return {
      background: 'transparent',
      hover: Colors.Topaze,
      color: Colors.King,
      border: 'transparent',
    }
  }

  if (color === 'alert' && variant === 'outline') {
    return {
      background: Colors.White,
      hover: Colors.Mew,
      color: Colors.Alert,
      border: `1px solid ${Colors.Alert}`,
    }
  }

  if (variant === 'light') {
    const lightTints: Record<string, { background: string; accent: string }> = {
      king: { background: Colors.Topaze, accent: Colors.King },
      platinum: { background: Colors.Cloud, accent: Colors.Ink },
      cobalt: { background: Colors.Ghost, accent: Colors.Cobalt },
      warning: { background: Colors.Peach, accent: Colors.Warning },
      alert: { background: Colors.Mew, accent: Colors.Cherry },
    }
    const tint = lightTints[color as string]
    if (tint) {
      return {
        background: tint.background,
        hover: tint.background,
        color: tint.accent,
        border: 'transparent',
      }
    }
  }

  return defaults
}

const activeStyles = {
  root: {
    '&:active:not([dataDisabled])': {
      backgroundColor: Colors.Navy,
      color: Colors.White,
    },
  },
}

export const mantineTheme = createTheme({
  primaryColor: 'king',

  variantColorResolver,

  colors: {
    king: colorsTuple(Colors.King),
    cobalt: colorsTuple(Colors.Cobalt),
    navy: colorsTuple(Colors.Navy),
    topaze: colorsTuple(Colors.Topaze),
    ghost: colorsTuple(Colors.Ghost),
    ink: colorsTuple(Colors.Ink),
    jet: colorsTuple(Colors.Jet),
    hurricane: colorsTuple(Colors.Hurricane),
    slateGrey: colorsTuple(Colors.SlateGrey),
    silver: colorsTuple(Colors.Silver),
    platinum: colorsTuple(Colors.Platinum),
    white: colorsTuple(Colors.White),
    cloud: colorsTuple(Colors.Cloud),
    mew: colorsTuple(Colors.Mew),
    alert: colorsTuple(Colors.Alert),
    success: colorsTuple(Colors.Success),
    warning: colorsTuple(Colors.Warning),
  },

  fontFamily: 'Roboto, sans-serif',

  headings: {
    fontFamily: 'Rubik, sans-serif',
    sizes: {
      h1: { fontSize: '1.25rem', lineHeight: '30px' },
      h2: { fontSize: '1rem', lineHeight: '26px' },
      h3: { fontSize: '0.875rem', lineHeight: '22px' },
    },
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },

  defaultRadius: 'sm',

  focusRing: 'auto',

  components: {
    Alert: {
      defaultProps: { variant: 'light' },
      styles: {
        root: { border: '1px solid var(--alert-color)', padding: '12px' },
        wrapper: { alignItems: 'flex-start' },
        body: { gap: 0 },
        icon: { marginInlineEnd: '4px' },
        title: { fontSize: '1rem', lineHeight: '26px', marginBottom: 0, color: Colors.Ink },
        message: { fontSize: '0.875rem', lineHeight: '22px', color: Colors.Ink },
      },
    },
    Anchor: {
      defaultProps: { underline: 'never', fw: 700 },
      classNames: {
        root: 'inline-flex items-center flex-nowrap min-w-0 cursor-pointer text-(--color-king) hover:text-(--color-cobalt) active:text-(--color-navy) data-[disabled=true]:text-(--color-slate-grey) data-[disabled=true]:cursor-default',
      },
    },
    Skeleton: {
      defaultProps: { width: '80%', height: 12, radius: 'sm' },
      classNames: { root: 'before:!bg-(--color-platinum) after:!bg-(--color-silver)' },
    },
    Badge: {
      classNames: {
        root: 'h-[1.625rem] px-2 normal-case text-sm data-[variant=small]:text-xs data-[variant=small]:h-5 data-[variant=small]:px-1.5',
        label: 'normal-case font-normal',
      },
    },
    Tabs: {
      defaultProps: { color: 'king' },
      classNames: {
        list: 'gap-2 border-b border-(--color-platinum)',
        tab: `relative font-bold text-base px-[10px] pt-0 pb-[3px] text-(--color-hurricane) data-[active=true]:text-(--color-jet) data-[disabled=true]:text-(--color-silver) data-[disabled=true]:cursor-not-allowed after:content-[''] after:absolute after:inset-x-0 after:bottom-0 after:h-1 after:rounded-t after:bg-transparent data-[active=true]:after:bg-(--color-king)`,
        tabSection: 'mr-1',
      },
      styles: {
        // The default Mantine Tabs indicator is a border-bottom, which can't render a rounded corner
        // (radius on a side with 0-width adjacent borders is invisible) — disable it and draw our own bar via ::after.
        // The default Mantine list divider (`--tab-border-color`) renders as a ~2px box (top+bottom border on
        // an auto-height box) — disable it too and draw our own exact 1px line via the `list` border-b above.
        root: { '--tabs-list-border-width': '0px', '--tab-border-color': 'transparent' } as React.CSSProperties,
      },
    },
    Button: { defaultProps: { size: 'compact-lg' }, styles: activeStyles },
    ActionIcon: { defaultProps: { size: 'md' }, styles: activeStyles },
    SegmentedControl: {
      styles: {
        root: { background: Colors.Topaze },
        indicator: { background: Colors.White },
      },
      classNames: { label: 'text-(--color-deep-sea) font-normal data-[active=true]:font-bold' },
    },
    DatePicker: {
      defaultProps: {
        hideOutsideDates: true,
        maxLevel: 'month',
        numberOfColumns: 2,
      },
    },
    Stepper: Stepper.extend({
      defaultProps: { color: 'king', size: 'sm', iconSize: 36 },
      styles: {
        separator: { backgroundColor: Colors.Platinum, marginInline: Sizes[16] },
        stepIcon: { borderWidth: 2, fontSize: Sizes[14] },
        stepLabel: { fontSize: Sizes[14], color: Colors.Jet },
      },
    }),
    Progress: Progress.extend({ defaultProps: { color: 'king' } }),
    Loader: Loader.extend({ defaultProps: { color: 'king' } }),
  },
})
